import { useMemo } from "react";
import { useUniversalEntityPageDataContext } from "../contexts/UniversalEntityPageDataContext";
import { useUniversalEntityPageStaticContext } from "../contexts/UniversalEntityPageStaticContext";
import { getModalConfig, isModalConfigured } from "@/lib/modals/url/modal-url-config";
import type { BaseEntity } from "../../UniversalCRMTable/types";

/**
 * Hook to compute detail modal navigation state (itemIds and currentItemIndex)
 * from context data and search parameters.
 *
 * This extracts the logic that was previously in an IIFE in UniversalEntityPageContent,
 * allowing EntityPageModals to access navigation state without prop drilling.
 *
 * The entity-id param name comes from `MODAL_URL_CONFIG`, which is what writes
 * it in the first place. Deriving it as `${entityName}Id` instead only agreed
 * with the config for single-word entities: `recurring-transactions` writes
 * `recurringTransactionsId` but was read back as `recurring-transactionsId`, so
 * the index never resolved and the modal's prev/next arrows stayed inert.
 */
export function useDetailModalNavigation<T extends BaseEntity = BaseEntity>(): {
  itemIds: string[];
  currentItemIndex: number | undefined;
} {
  const { pageConfig } = useUniversalEntityPageStaticContext<T>();
  const { dataState, navigation } = useUniversalEntityPageDataContext<T>();
  const { entityName } = pageConfig;
  const { processedData } = dataState;
  const { searchParams } = navigation;

  return useMemo(() => {
    const itemIds = processedData?.map((item: T) => item.id) || [];
    const modalId = `${entityName}-detail`;
    const entityIdParam = isModalConfigured(modalId)
      ? getModalConfig(modalId).pluralParam
      : `${entityName}Id`;
    const currentItemIdParam = searchParams?.get(entityIdParam);
    const currentItemIndex = currentItemIdParam ? itemIds.indexOf(currentItemIdParam) : -1;

    return {
      itemIds,
      currentItemIndex: currentItemIndex >= 0 ? currentItemIndex : undefined,
    };
  }, [processedData, searchParams, entityName]);
}
